import React, { useState, useRef, useEffect } from "react";
import styles from "./task-list-header.module.css";
import { columnsType, Task } from "../../types/public-types";
import SortingICon from "@/assets/svg/SortingICon.svg";
import { useTranslation } from "react-i18next";

export const TaskListHeaderDefault: React.FC<{
  headerHeight: number;
  rowWidth: string;
  fontFamily: string;
  fontSize: string;
  columns: columnsType[];
  setColumns?: (columns: columnsType[]) => void;
  sortField: string | null;
  sortOrder: "asc" | "desc";
  onSort: (field: keyof Task, type?: string) => void;
  bulkMultiSelectActive?: boolean;
}> = ({
  headerHeight,
  fontFamily,
  fontSize,
  rowWidth,
  columns,
  setColumns,
  sortField,
  sortOrder,
  onSort,
  bulkMultiSelectActive = false,
}) => {
  const [isResizing, setIsResizing] = useState(false);
  const [currentColumnIndex, setCurrentColumnIndex] = useState<number | null>(
    null
  );
  const [dropIndicatorIndex, setDropIndicatorIndex] = useState<number | null>(
    null
  );
  const startXRef = useRef<number>(0);
  const startWidthRef = useRef<number>(0);
  const dragColIndex = useRef<number | null>(null);

  // === Resize Handlers ===
  const handleMouseDown = (e: React.MouseEvent, index: number) => {
    setIsResizing(true);
    setCurrentColumnIndex(index);
    startXRef.current = e.clientX;
    startWidthRef.current = columns[index].width || parseInt(rowWidth);
  };

  const handleMouseMove = (e: MouseEvent) => {
    if (isResizing && currentColumnIndex !== null) {
      const diffX = e.clientX - startXRef.current;
      const newWidth = Math.max(startWidthRef.current + diffX, 50);
      const newColumns = columns.map((col, idx) =>
        idx === currentColumnIndex ? { ...col, width: newWidth } : col
      );
      setColumns && setColumns(newColumns);
    }
  };

  const handleMouseUp = () => {
    setIsResizing(false);
    setCurrentColumnIndex(null);
  };

  useEffect(() => {
    if (isResizing) {
      document.addEventListener("mousemove", handleMouseMove);
      document.addEventListener("mouseup", handleMouseUp);
    }
    return () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
    };
  }, [isResizing, columns]);

  // === Drag & Drop Handlers ===
  const handleDragStart = (index: number) => {
    if (!isResizing) {
      dragColIndex.current = index;
    }
  };

  const handleDragOver = (e: React.DragEvent, index: number) => {
    e.preventDefault();
    if (!isResizing && dragColIndex.current !== null) {
      setDropIndicatorIndex((prev) => (prev !== index ? index : prev));
    }
  };

  const handleDrop = (dropIndex: number) => {
    if (!isResizing) {
      const dragIndex = dragColIndex.current;
      if (dragIndex === null || dragIndex === dropIndex) return;

      const reordered = [...columns];
      const [draggedCol] = reordered.splice(dragIndex, 1);
      reordered.splice(dropIndex, 0, draggedCol);
      setColumns && setColumns(reordered);

      dragColIndex.current = null;
      setDropIndicatorIndex(null);
    }
  };

  const handleDragEnd = () => {
    dragColIndex.current = null;
    setDropIndicatorIndex(null);
  };

  const visibleColumns = columns.filter((col) => !col.hidden);

  const getPreviewColumns = () => {
    if (
      dragColIndex.current !== null &&
      dropIndicatorIndex !== null &&
      dragColIndex.current !== dropIndicatorIndex
    ) {
      const cols = [...visibleColumns];
      const [dragged] = cols.splice(dragColIndex.current, 1);
      cols.splice(dropIndicatorIndex, 0, dragged);
      return cols.map((col, i) => ({
        ...col,
        _isGhost: i === dropIndicatorIndex,
      }));
    }
    return visibleColumns;
  };

  const previewColumns = getPreviewColumns();
  const { t } = useTranslation();

  // === Context Menu Logic ===
  type ContextMenuPosition = { x: number; y: number; column: string };
  const [visible, setVisible] = useState(false);
  const [position, setPosition] = useState<ContextMenuPosition>({
    x: 0,
    y: 0,
    column: "",
  });
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const hideMenu = () => setVisible(false);
    document.addEventListener("click", hideMenu);
    window.addEventListener("scroll", hideMenu);
    window.addEventListener("resize", hideMenu);
    return () => {
      document.removeEventListener("click", hideMenu);
      window.removeEventListener("scroll", hideMenu);
      window.removeEventListener("resize", hideMenu);
    };
  }, []);

  const removeColumn = (field: string) => {
    if (field && setColumns) {
      let  hidden_columns :string ="";
       if (window.location.pathname !== "/projects") {
        hidden_columns = localStorage.getItem("hidden_columns") ?? ""
      }else{
        hidden_columns = localStorage.getItem("projects_hidden_columns") ?? ""
      }
       
      const updated = [...JSON.parse(hidden_columns), field];
      console.log(updated);
      if (window.location.pathname !== "/projects") {
        localStorage.setItem("hidden_columns", JSON.stringify(updated));
      }else{
        localStorage.setItem("projects_hidden_columns", JSON.stringify(updated));
      }
      setColumns(columns.filter((col) => col.field !== field));
    }
  };

  return (
    <div
      className={
        styles.ganttTable +
        " w-full select-none border-b-2 border-b-gray-200 relative"
      }
      style={{ fontFamily, fontSize }}
      onContextMenu={(e) => {
        e.preventDefault();

        const target = e.target as HTMLDivElement;
        const column = target.getAttribute("data-column") ?? "";

        console.log(e);

        // Mouse position relative to the page (accurate even if resized)
        const posX = e.clientX; // or rect.left + (e.clientX - rect.left)
        const posY = e.clientY;

        // Optional: Ensure menu doesn't overflow the window
        const menuWidth = 200; // adjust to your menu width
        const menuHeight = 150; // adjust to your menu height
        let finalX = posX;
        let finalY = posY;

        if (finalX + menuWidth > window.innerWidth) {
          finalX = window.innerWidth - menuWidth - 5;
        }
        if (finalY + menuHeight > window.innerHeight) {
          finalY = window.innerHeight - menuHeight - 5;
        }
        if (column !== "add new column") {
          setPosition({ x: finalX - 30, y: finalY - 110, column });
          setVisible(true);
        }
      }}
    >
      <div
        className={
          styles.ganttTable_Header +
          " " +
          styles.bulkHeaderRow +
          (bulkMultiSelectActive ? " " + styles.bulkActive : "") +
          " flex flex-row items-stretch "
        }
        style={{ height: headerHeight - 2 }}
      >
        {previewColumns.map((col: columnsType, index: number) => (
          <div
            key={`${index}-${String(col.field)}`}
            data-column={col.field}
            className={`flex items-stretch justify-start hover:bg-gray-200 transition-colors duration-150 relative`}
            draggable={!isResizing && !col.nonDragable}
            onDragStart={() => handleDragStart(index)}
            onDragEnd={handleDragEnd}
            onDragOver={(e) => handleDragOver(e, index)}
            onDrop={() => handleDrop(index)}
            style={{
              width: `${col.width || parseInt(rowWidth)}px`,
              flexShrink: 0,
              flexGrow: 0,
            }}
          >
            <div
              className={
                styles.ganttTable_HeaderItem +
                " text-center font-semibold text-[#787486] cursor-pointer flex items-center justify-center gap-1 transition-all duration-100 ease-in-out " +
                ((col as any)._isGhost
                  ? "opacity-40 rounded-none text-black bg-slate-300"
                  : "")
              }
              data-column={col.field}
              style={{ width: "100%" }}
              onClick={() => col.sortable && onSort(col.field as keyof Task)}
            >
              {col.headerRender ? col.headerRender() : <>{col.name}</>}
              {col.sortable && (
                <img
                  src={SortingICon}
                  className={`${
                    sortField === col.field && sortOrder === "asc"
                      ? "rotate-180"
                      : ""
                  } ${sortField === col.field ? "invert" : "brightness-75"}`}
                />
              )}
            </div>
            <div
              className={styles.ganttTable_HeaderSeparator}
              style={{
                height: headerHeight * 0.5,
                marginTop: headerHeight * 0.2,
                cursor: "col-resize",
              }}
              onMouseDown={(e) => handleMouseDown(e, index)}
            />
            {visible && position.column == col.field && (
              <div
                ref={menuRef}
                style={{
                  position: "absolute",
                  top: headerHeight - 2,
                  left: "50%",
                  transform: "translate(-50%, 0)", 
                  background: "#fff",
                  border: "1px solid #ccc",
                  boxShadow: "2px 2px 6px rgba(0,0,0,0.2)",
                  padding: "10px",
                  zIndex: 1000,
                  width:"230px"
                }}
                className="rounded-md flex flex-col gap-2"
              >
                <div
                  className="px-2 py-0.5 text-gray-450 hover:bg-slate-200 rounded cursor-pointer"
                  onClick={() => removeColumn(position.column)}
                >
                  {t("Hide column")}
                </div>
                <div
                  className="px-2 py-0.5 text-gray-450 hover:bg-slate-200 rounded cursor-pointer"
                  onClick={() => onSort(position.column as keyof Task, "asc")}
                >
                  {t("Sort ascending")}
                </div>
                <div
                  className="px-2 py-0.5 text-gray-450 hover:bg-slate-200 rounded cursor-pointer"
                  onClick={() => onSort(position.column as keyof Task, "dasc")}
                >
                  {t("Sort descending")}
                </div>
              </div>
            )}
          </div>
        ))}

        {/* Fallback drop zone at end */}
        <div
          onDragOver={(e) => handleDragOver(e, previewColumns.length)}
          onDrop={() => handleDrop(previewColumns.length)}
          style={{ width: "2px" }}
        />
      </div>

      {/* Custom Context Menu */}
    </div>
  );
};
